import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { formatNumber } from "@/lib/format";

export function VdbEditDialog({ product, open, onOpenChange, onSaved }) {
  const { role } = useRole();
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && product) setValue(String(product.stock_vandenbrink ?? 0));
  }, [open, product]);

  if (!product) return null;
  const total = product.stock_wc_total ?? product.stock_total;
  const qty = Number(String(value).replace(",", "."));
  const valid = value !== "" && !Number.isNaN(qty) && qty >= 0;
  const tooMuch = valid && qty > 0 && qty > total;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/products/${product.id}/vandenbrink`, { quantity: qty, role: role.id });
      toast.success("Voorraad Van den Brink opgeslagen");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Niet opgeslagen", { description: e.response?.data?.detail || "Probeer het opnieuw." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vdb-edit-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Voorraad bij Van den Brink</DialogTitle>
          <DialogDescription>{product.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Totale webshopvoorraad: <span className="font-mono font-semibold text-foreground" data-testid="vdb-dialog-total">{formatNumber(total, 1)} {product.unit}</span>
          </div>
          <label className="block">
            <span className="label-eyebrow">Hoeveelheid fysiek in Zeewolde</span>
            <Input data-testid="vdb-input" type="number" inputMode="decimal" min={0} step="any" autoFocus
              value={value} onChange={(e) => setValue(e.target.value)} className="h-14 text-2xl font-mono mt-2" />
          </label>
          {tooMuch ? (
            <div className="text-sm text-status-critical" data-testid="vdb-too-much">
              Dit is meer dan de totale webshopvoorraad ({formatNumber(total, 1)}). Opslaan is niet mogelijk.
            </div>
          ) : valid && (
            <div className="text-sm" data-testid="vdb-calc-kk">
              Kipkruiden wordt dan: <span className="font-mono font-semibold">{formatNumber(total - qty, 1)} {product.unit}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vdb-cancel">Annuleren</Button>
          <Button onClick={save} disabled={!valid || tooMuch || saving} data-testid="vdb-save">
            {saving ? "Opslaan…" : "Opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
